import React from 'react';
import PropTypes from 'prop-types';
import {Paper, Backdrop, useMediaQuery} from '@mui/material';
import {makeStyles} from 'tss-react/mui';
import Loader from 'wobi-web-common/dist/components/Loader';
import OffersContainer, {OffersPageHeader} from '../offers/OffersContainer';
import NoOffers from '../offers/NoOffers';
import ErrorMessagePopup from '../../components/modals/ErrorMessage';
import {getImageByAgent} from '../../config/agent';
import {SidebarCoveragePanel} from '../../components/SidebarCoveragePanel.jsx';
import {MobileCoverageSheet} from '../../components/MobileCoverageSheet.jsx';
import {Breakpoints} from 'wobi-web-common';
import {useIntl} from 'react-intl';

const LoaderTravelImage = getImageByAgent('loader-travel.gif');

const OffersList = React.memo(
  ({
    className,
    offers,
    loaderState,
    keepChecking,
    submitAct,
    isSubmitting,
    handleErrorClose,
    isErrorOpen,
    errorMessageId,
    offersRequestFailed,
    handleNoOffers,
    formikSetField,
    formikErrors,
    formikValues,
    handleUpdateRiders,
    onRidersChange,
    hasPendingRiderChanges,
    quotedFormValues,
    validateForm,
    onEditDetails,
  }) => {
    const {classes, cx} = useStyles();
    const isDesktop = !useMediaQuery('(max-width:900px)');
    const isMobile = useMediaQuery(theme => theme.breakpoints.down(Breakpoints.mobile));
    const [isCoverageSheetOpen, setIsCoverageSheetOpen] = React.useState(false);
    const intl = useIntl();

    React.useEffect(() => {
      if (!isMobile) setIsCoverageSheetOpen(false);
    }, [isMobile]);

    const offersContainerProps = {
      offers,
      submitAct,
      isSubmitting,
      hasPendingRiderChanges,
    };
    const noOffersProps = {
      handleNoOffers,
      onEditDetails,
    };

    return (
      <div className={className} data-testid='offers-main-container'>
        <Backdrop
          className={classes.backdrop}
          open={loaderState}
          data-testid='offers-loader'
        >
          <Paper style={{padding: 30, borderRadius: 16}}>
            <Loader image={LoaderTravelImage}>
              <span>
                {intl.formatMessage({id: 'offers.loader.title'})}
              </span>
              <br />
              <span style={{fontSize: 20}}>
                {intl.formatMessage({id: 'offers.loader.description'})}
              </span>
            </Loader>
          </Paper>
        </Backdrop>
        {offers.length ? (
          <OffersPageHeader
            contentFrameClassName={classes.contentFrame}
            offersCount={Object.keys(offers).length}
            onEditDetails={onEditDetails}
            onOpenCoverage={() => setIsCoverageSheetOpen(true)}
            values={formikValues}
          />
        ) : null}

        {isMobile && (
          <MobileCoverageSheet
            open={isCoverageSheetOpen}
            onClose={() => setIsCoverageSheetOpen(false)}
            formikValues={formikValues}
            quotedFormValues={quotedFormValues}
            formikSetField={formikSetField}
            formikErrors={formikErrors}
            validateForm={validateForm}
            onUpdateRiders={handleUpdateRiders}
            onRidersChange={onRidersChange}
          />
        )}

        <div className={cx(classes.contentFrame, classes.mainLayout)} dir='rtl'>
          {isDesktop && (
            <aside className={classes.coveragePanel}>
              <SidebarCoveragePanel
                formikValues={formikValues}
                quotedFormValues={quotedFormValues}
                formikSetField={formikSetField}
                formikErrors={formikErrors}
                validateForm={validateForm}
                onUpdateRiders={handleUpdateRiders}
                onRidersChange={onRidersChange}
              />
            </aside>
          )}

          <div className={classes.offersColumn}>
            {offers.length ? (
              <OffersContainer {...offersContainerProps} />
            ) : !keepChecking && !offers.length && !offersRequestFailed ? (
              <NoOffers {...noOffersProps} />
            ) :
              false
            }
            <ErrorMessagePopup
              onClose={handleErrorClose}
              isOpen={isErrorOpen}
              messageId={errorMessageId}
            />
          </div>
        </div>
      </div>
    );
  },
);

const useStyles = makeStyles()((theme) => ({
  contentFrame: {
    width: '100%',
    maxWidth: 1180,
    margin: '0 auto',
    paddingInline: 'clamp(16px, 3vw, 28px)',
  },
  mainLayout: {
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
    paddingBottom: 20,
  },
  coveragePanel: {
    width: 300,
    flex: 'none',
    position: 'sticky',
    top: 83,
  },
  offersColumn: {
    flex: 1,
    minWidth: 0,
  },
  backdrop: {
    zIndex: 9,
  },
}));

OffersList.propTypes = {
  offers: PropTypes.object,
  submitAct: PropTypes.func,
  coversTitles: PropTypes.object,
  className: PropTypes.object,
  loaderState: PropTypes.bool,
  keepChecking: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  handleErrorClose: PropTypes.func,
  isErrorOpen: PropTypes.bool,
  errorMessageId: PropTypes.string,
  offersRequestFailed: PropTypes.bool,
  formikErrors: PropTypes.object,
  handleNoOffers: PropTypes.func,
  formikSetField: PropTypes.func,
  formikValues: PropTypes.object,
  handleUpdateRiders: PropTypes.func,
  onRidersChange: PropTypes.func,
  hasPendingRiderChanges: PropTypes.bool,
  quotedFormValues: PropTypes.object,
  onEditDetails: PropTypes.func.isRequired,
  validateForm: PropTypes.func,
};

export default OffersList;
